import { useEffect, useRef, useState } from 'react';

interface UseInViewOptions {
    threshold?: number;
    rootMargin?: string;
    triggerOnce?: boolean;
}

export function useInView<T extends HTMLElement = HTMLDivElement>(options: UseInViewOptions = {}) {
    const [isInView, setIsInView] = useState(false);
    const [hasTriggered, setHasTriggered] = useState(false);
    const ref = useRef<T>(null);

    const { threshold = 0.1, rootMargin = '0px', triggerOnce = true } = options;

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const observer = new IntersectionObserver(
            ([entry], obs) => {
                const inView = entry.isIntersecting;

                if (triggerOnce) {
                    // For one-time triggers, only update on first enter
                    if (inView && !hasTriggered) {
                        setIsInView(true);
                        setHasTriggered(true);
                        obs.unobserve(entry.target);
                    }
                } else {
                    // For repeated triggers, always reflect current visibility
                    setIsInView(inView);
                }
            },
            { threshold, rootMargin }
        );

        observer.observe(element);
        return () => observer.unobserve(element);
    }, [threshold, rootMargin, triggerOnce, hasTriggered]);

    return [ref, triggerOnce ? (hasTriggered || isInView) : isInView] as const;
} 
